/**
 * RewardsStoreRoute — `/belonninger`, Belønninger-fanen i Premier (#156,
 * steg 6a; Stell_Premier).
 *
 * Fra toppen:
 *   - PremierFrame: ScreenHeader «Premier» med avataren, og underfanene;
 *   - lommebokkortet, som lenke til `/poeng`: saldo (`useBalance`), ukas poeng
 *     fra den innloggedes rad på ukestavla, streak og frysninger. Ingenting
 *     lagres ved siden av: saldoen er fortsatt SUM over hovedboka
 *     (Anti-Pattern 6), og ukas poeng er tavlas egen SUM. Kortet står
 *     mellom toppen og underfanene, slik skissen viser (`wallet` i rammen);
 *   - belønningene i to kolonner (tre på brede skjermer);
 *   - «Mine bytter».
 *
 * Auth + viewer:
 *   GET /api/auth/me gir medlemmet. Alle innloggede (barn/tenåring/voksen)
 *   kan se butikken.
 *
 * Layer 2 (D-11):
 *   useRewardsForMember(viewerId) treffer GET /api/rewards?for_member=<id>,
 *   og serveren filtrerer bort det barnet ikke skal se. UI-et filtrerer ikke
 *   på nytt.
 *
 * Mine bytter:
 *   Kravene har bare `reward_id` og `approved_by`. Tittelen slås opp i
 *   belønningslisten ruten allerede har, og navnet på den som avslo i
 *   /api/members/basic (useFamilyMembers).
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { EmptyState } from '../../components/primitives';
import { FlameChip } from '../../components/primitives/FlameChip';
import { Icon } from '../../components/primitives/Icon';
import { Pill } from '../../components/primitives/Pill';
import { formatPoints } from '../../i18n/formatters';
import { useFamilyMembers } from '../chores/useFamilyMembers';
import { useWeeklyLeaderboard } from '../leaderboard/useLeaderboardQueries';
import { useStreak } from '../streaks/useStreakQueries';
import { PremierFrame, WALLET_SURFACE } from './PremierFrame';
import { RewardCard } from './RewardCard';
import { RewardClaimSheet } from './RewardClaimSheet';
import { RewardClaimsHistory } from './RewardClaimsHistory';
import {
  useMyRewardClaims,
  useRewardsForMember,
  type RewardRow,
} from './useRewardsQueries';
import { useBalance } from '../points/usePointsQueries';

interface SessionMember {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
}

function useSession(): { viewer: SessionMember | null; loaded: boolean } {
  const [viewer, setViewer] = useState<SessionMember | null>(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', {
          credentials: 'same-origin',
        });
        if (!r.ok) {
          if (!cancelled) setLoaded(true);
          return;
        }
        const body = (await r.json()) as { member?: SessionMember };
        if (!cancelled) {
          setViewer(body.member ?? null);
          setLoaded(true);
        }
      } catch {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { viewer, loaded };
}

/**
 * Lommebokkortet: saldo, ukas poeng, streak og frysninger, som lenke til
 * lommeboken. Chipene vises bare når det er noe å vise, som på Hjem.
 */
function WalletSummary({
  memberId,
  balance,
}: {
  memberId: string;
  balance: number | undefined;
}): ReactNode {
  const { t } = useTranslation();
  const { data: streak } = useStreak(memberId);
  const { data: weekly } = useWeeklyLeaderboard();
  const weekPoints = weekly?.find((row) => row.memberId === memberId)?.points;
  const days = streak?.current_streak ?? 0;
  const freezes = streak?.streak_freezes_balance ?? 0;
  return (
    <Link
      to="/poeng"
      data-testid="rewards-wallet-card"
      className={`${WALLET_SURFACE} flex items-center gap-3.5 px-4 py-3.5 md:max-w-xl`}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-caption font-bold text-ink-2">{t('rewards.wallet.label')}</span>
        <span
          data-testid="rewards-wallet-balance"
          className="tnum block text-wallet font-extrabold"
        >
          {typeof balance === 'number'
            ? t('rewards.points', { points: formatPoints(balance) })
            : t('rewards.wallet.pending')}
        </span>
        {weekPoints !== undefined ? (
          <span data-testid="rewards-wallet-week" className="tnum block text-caption font-extrabold text-good">
            {t('rewards.wallet.week', { points: formatPoints(weekPoints) })}
          </span>
        ) : null}
      </span>
      {days > 0 || freezes > 0 ? (
        <span className="flex shrink-0 flex-col items-end gap-1.5">
          {days > 0 ? <FlameChip days={days} /> : null}
          {freezes > 0 ? (
            <span data-testid="rewards-wallet-freezes">
              <Pill variant="blue">{t('rewards.wallet.freezes', { count: freezes })}</Pill>
            </span>
          ) : null}
        </span>
      ) : null}
      <Icon.ChevR size={20} className="shrink-0" />
    </Link>
  );
}

export function RewardsStoreRoute(): ReactNode {
  const { t } = useTranslation();
  const { viewer, loaded } = useSession();
  const memberId = viewer?.id ?? null;

  const rewardsQuery = useRewardsForMember(memberId);
  const balanceQuery = useBalance(memberId ?? '', !!memberId);
  const claimsQuery = useMyRewardClaims(memberId);
  const membersQuery = useFamilyMembers(!!memberId);

  const [activeReward, setActiveReward] = useState<RewardRow | null>(null);

  const rewards = rewardsQuery.data;
  const members = membersQuery.data;
  const rewardTitles = useMemo(
    () => new Map((rewards ?? []).map((r) => [r.id, r.title])),
    [rewards],
  );
  const memberNames = useMemo(
    () => new Map((members ?? []).map((m) => [m.id, m.short_name])),
    [members],
  );

  if (!loaded) {
    return (
      <PremierFrame testId="rewards-store-loading">
        <p className="m-0 text-sm text-ink-2">{t('rewards.loading')}</p>
      </PremierFrame>
    );
  }

  if (!viewer) {
    return (
      <PremierFrame testId="rewards-store-403">
        <p className="m-0 py-8 text-center text-sm text-ink-2">{t('rewards.notAuthFallback')}</p>
      </PremierFrame>
    );
  }

  // Ukjent saldo (henter eller feilet) er ikke 0: kortene viser da bare prisen,
  // i stedet for å blinke som «har ikke råd».
  const balance = balanceQuery.data;
  const claims = claimsQuery.data ?? [];

  return (
    <PremierFrame testId="rewards-store" wallet={<WalletSummary memberId={viewer.id} balance={balance} />}>
      {/* Plan 06-02 Task 2: <EmptyState variant="inline"> med
          rewards.empty.store.{heading,body}. Ytre element bærer
          rewards-store-empty-kontrakten. */}
      {!rewards || rewards.length === 0 ? (
        <section data-testid="rewards-store-empty">
          <EmptyState
            variant="inline"
            heading={t('rewards.empty.store.heading')}
            body={t('rewards.empty.store.body')}
          />
        </section>
      ) : (
        <section data-testid="rewards-store-grid" className="grid grid-cols-2 gap-2.5 lg:grid-cols-3">
          {rewards.map((reward) => (
            <RewardCard
              key={reward.id}
              reward={reward}
              balance={balance}
              onClaim={() => setActiveReward(reward)}
            />
          ))}
        </section>
      )}

      <section className="mt-1 flex flex-col gap-3">
        <h2 className="m-0 text-caption font-bold tracking-wider text-ink-2 uppercase">
          {t('rewards.history.heading')}
        </h2>
        <RewardClaimsHistory claims={claims} rewardTitles={rewardTitles} memberNames={memberNames} />
      </section>

      {activeReward ? (
        <RewardClaimSheet
          reward={activeReward}
          viewerId={viewer.id}
          // Arket åpnes bare fra «Bytt», som krever kjent saldo.
          balance={balance ?? 0}
          open={true}
          onClose={() => setActiveReward(null)}
        />
      ) : null}
    </PremierFrame>
  );
}
